import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, MapPin } from 'lucide-react';
import { siteConfig } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from './LanguageToggle';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.ourWork'), path: '/programs' },
    { name: t('nav.news'), path: '/news' },
    { name: t('nav.legalHelp'), path: '/legal-help' },
    { name: t('nav.partners'), path: '/partners' },
    { name: t('nav.contact'), path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-amber-50/95 backdrop-blur-md border-b border-amber-200/50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src={siteConfig.logo} 
              alt="Xicano Rights Union"
              className="h-14 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative font-medium transition-colors duration-300 ${
                  isActive(link.path)
                    ? 'text-teal-600'
                    : 'text-stone-700 hover:text-teal-600'
                }`}
              >
                {link.name}
                {isActive(link.path) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-teal-600 rounded-full" />
                )}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageToggle variant="header" />
            <div className="flex flex-col gap-1">
              <Link
                to="/alert-map"
                className={`px-4 py-2 flex items-center gap-2 font-semibold rounded-lg transition-all duration-300 ${
                  isActive('/alert-map')
                    ? 'bg-red-600 text-white'
                    : 'bg-red-500 hover:bg-red-600 text-white'
                }`}
              >
                <MapPin size={16} />
                <span>{t('nav.alertMap')}</span>
                <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
              </Link>
              <Link
                to="/report-violation"
                className="px-4 py-1.5 text-xs text-center bg-purple-100 text-purple-700 hover:bg-purple-200 font-medium rounded-lg transition-colors"
              >
                {t('nav.reportViolation')}
              </Link>
            </div>
            <Link
              to="/join"
              className="px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-teal-600/25"
            >
              {t('nav.joinXRU')}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <LanguageToggle variant="header" />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-stone-700 hover:text-teal-600 transition-colors"
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-amber-50/98 border-t border-amber-200/50">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block py-3 px-4 rounded-lg font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-teal-100 text-teal-700'
                    : 'text-stone-700 hover:bg-teal-50 hover:text-teal-600'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4 space-y-3">
              <Link
                to="/alert-map"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-red-500 text-white font-semibold rounded-lg transition-colors hover:bg-red-600"
              >
                <MapPin size={18} />
                {t('nav.alertMap')}
                <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
              </Link>
              <Link
                to="/report-violation"
                onClick={() => setIsMenuOpen(false)}
                className="block w-full text-center py-3 px-4 border-2 border-purple-500 text-purple-600 font-semibold rounded-lg transition-colors hover:bg-purple-50"
              >
                {t('nav.reportViolation')}
              </Link>
              <Link
                to="/join"
                onClick={() => setIsMenuOpen(false)}
                className="block w-full text-center py-3 px-4 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors"
              >
                {t('nav.joinXRU')}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
